/**
 * Главный компонент административного кабинета
 */

import React, { useState } from 'react';
import Header from '../components/UI/Header';
import { ClientSearchPanel } from './AdminOffice/components/ClientSearchPanel';
import { IssueSection } from './AdminOffice/components/IssueSection';
import { RepaymentsSection } from './AdminOffice/components/RepaymentsSection';
import { EditClientSection } from './AdminOffice/components/EditClientSection';
import { ReferencesSection } from './AdminOffice/components/ReferencesSection';
import { AddUserSection } from './AdminOffice/components/AddUserSection';
import { OtherSection } from './AdminOffice/components/OtherSection';
import { AdminSection } from '@types';
import type { ClientData, Deal } from '@types';
import styles from './AdminOffice.module.css';

/**
 * Конфигурация секций для навигации
 */
const SECTIONS = [
  { key: AdminSection.Issue, label: 'Выдача/Закрытие займов' },
  { key: AdminSection.Repayments, label: 'Погашения' },
  { key: AdminSection.EditingClientData, label: 'Редактирование данных клиента' },
  { key: AdminSection.References, label: 'Загрузка справочников' },
  { key: AdminSection.AddUser, label: 'Добавление пользователей' },
  { key: AdminSection.Other, label: 'Прочее' },
] as const;

/**
 * Главный компонент AdminOffice
 */
const AdminOffice: React.FC = () => {
  const [activeSection, setActiveSection] = useState<AdminSection>(AdminSection.Issue);
  const [clientId, setClientId] = useState<number | null>(null);
  const [client, setClient] = useState<ClientData | null>(null);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [creditContractId, setCreditContractId] = useState<number | null>(null);

  const handleClientSelect = (clientData: ClientData, _deals: Deal[]) => {
    setClient(clientData);
    setClientId(clientData.id);
    setSelectedClientName(
      `${clientData.lastName} ${clientData.firstName} ${clientData.middleName}`
    );
    // Сбрасываем выбранную сделку при смене клиента
    setCreditContractId(null);
  };

  const handleDealSelect = (dealId: number | null) => {
    setCreditContractId(dealId);
  };

  const renderSection = () => {
    switch (activeSection) {
      case AdminSection.Issue:
        return <IssueSection creditContractId={creditContractId} />;
      case AdminSection.Repayments:
        return (
          <RepaymentsSection
            creditContractId={creditContractId}
            selectedClientName={selectedClientName}
          />
        );
      case AdminSection.EditingClientData:
        return <EditClientSection clientData={client} />;
      case AdminSection.References:
        return <ReferencesSection />;
      case AdminSection.AddUser:
        return <AddUserSection />;
      case AdminSection.Other:
        return <OtherSection creditContractId={creditContractId} />;
      default:
        return <IssueSection creditContractId={creditContractId} />;
    }
  };

  return (
    <div>
      <Header title="Кабинет администратора" />

      <div className={styles.adminOffice}>
        {/* Левая панель навигации */}
        <aside className={styles.sidebar}>
          <nav className={styles.navigation}>
            <ul className={styles.navList}>
              {SECTIONS.map((section) => (
                <li
                  key={section.key}
                  className={`${styles.navItem} ${
                    activeSection === section.key ? styles.active : ''
                  }`}
                  onClick={() => setActiveSection(section.key)}
                  role="button"
                  tabIndex={0}
                  aria-current={activeSection === section.key ? 'page' : undefined}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveSection(section.key);
                    }
                  }}
                >
                  {section.label}
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Центральная контентная область */}
        <main className={styles.content}>
          <div className={styles.contentInner}>
            {renderSection()}
          </div>
        </main>

        {/* Правая панель поиска клиента */}
        <aside className={styles.clientPanel}>
          <div className={styles.clientPanelSticky}>
            <div className={styles.clientPanelCard}>
              <div className={styles.clientPanelTitle}>
                {!clientId ? (
                  <span className={styles.clientNotSelected}>
                    🔍 Клиент не выбран
                  </span>
                ) : (
                  <>
                    <span className={styles.clientSelected}>
                      👤 {selectedClientName}
                    </span>
                    <div style={{ fontSize: '14px', color: '#757575', marginTop: '4px' }}>
                      CreditContractId: {creditContractId || 'не выбрана'}
                    </div>
                  </>
                )}
              </div>
              <ClientSearchPanel
                clientId={clientId}
                selectedClientName={selectedClientName}
                creditContractId={creditContractId}
                onClientSelect={handleClientSelect}
                onDealSelect={handleDealSelect}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default AdminOffice;
